import type { Accessorials } from "@/Shared/types";
import type { AccessorialType } from "@/Data/generateAccessorialTypes";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { LocationType } from "@freightsimple/generated-dashboard-openapi-client/models/LocationType";
import { Checkbox } from "@freightsimple/ui-core/Controls/Checkbox";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";
import type { JSX, ReactNode } from "react";
import React, { useEffect, useState } from "react";

const CONFIRM_REMOVAL_TITLE = "Confirm Special Service Removal";

function challengeMessage(line1: string, line2: string): ReactNode {
  return (
    <Stack gap={4} align="start">
      <T>{line1}</T>
      <T>{line2}</T>
      <T>Are you sure you want to remove this?</T>
    </Stack>
  );
}

type AccessorialsInputProps = {
  accessorials: Array<AccessorialType>;
  selectedAccessorials: Accessorials;
  selectedAccessorialsChanged: (_: Accessorials) => void;
  locationContext: LocationContext;
  locationType: LocationType;
};

const LocationsAccessorialsInput: React.FC<AccessorialsInputProps> = (
  props: AccessorialsInputProps,
) => {
  const [selectedAccessorials, setSelectedAccessorials] = useState<
    Array<string>
  >(props.selectedAccessorials);

  useEffect(
    function () {
      setSelectedAccessorials(props.selectedAccessorials);
    },
    [props.selectedAccessorials],
  );

  function isAccessorialSelected(type: string): boolean {
    return selectedAccessorials.includes(type);
  }

  // Some services are strongly recommended for a location type — taking one
  // back off is what earns an "are you sure", because the carrier will often
  // need it anyway and bill for it later.
  function describeChallengeRemoval(type: string): ReactNode | undefined {
    if (props.locationType === LocationType.DistributionWarehouse) {
      if (type === "SCHEDULING_APPOINTMENT_REQUIRED") {
        return challengeMessage(
          "Most distribution warehouses have strict requirements about appointments.",
          "Additional charges may apply later if the carrier is required to make an appointment",
        );
      }
    }

    if (props.locationType === LocationType.Residential) {
      if (type === "SCHEDULING_APPOINTMENT_REQUIRED") {
        return challengeMessage(
          "Most residential deliveries require appointments to ensure the resident is at home.",
          "Additional charges may apply later if the carrier is required to make an appointment",
        );
      }

      if (type === "LOGISTICS_LIFT_GATE_REQUIRED") {
        return props.locationContext === LocationContext.Delivery
          ? challengeMessage(
              "Most residential deliveries require liftgates to offload the freight as most residences do not have loading docks",
              "Additional charges may apply later if the carrier is required to use a liftgate to complete the delivery",
            )
          : challengeMessage(
              "Most residential pickups require liftgates to offload the freight as most residences do not have loading docks",
              "Additional charges may apply later if the carrier is required to use a liftgate to complete the pickup",
            );
      }
    }

    if (
      type === "LOGISTICS_LIFT_GATE_REQUIRED" &&
      selectedAccessorials.includes("LOGISTICS_INSIDE")
    ) {
      return props.locationContext === LocationContext.Pickup
        ? challengeMessage(
            "Inside pickup service requires a lift gate. The carrier must unload freight from the truck and bring it inside the location.",
            "Removing the lift gate may result in additional charges if the carrier cannot complete the inside pickup service.",
          )
        : challengeMessage(
            "Inside delivery service requires a lift gate. The carrier must unload freight from the truck and bring it inside the location.",
            "Removing the lift gate may result in additional charges if the carrier cannot complete the inside delivery service.",
          );
    }

    return undefined;
  }

  function handleChange(type: string, checked: boolean): void {
    let newValue = checked
      ? [...selectedAccessorials, type]
      : selectedAccessorials.filter((o) => o !== type);

    // Inside service can't happen without a lift gate to get the freight off
    // the truck, so turning it on turns that on too.
    if (type === "LOGISTICS_INSIDE" && checked) {
      if (!newValue.includes("LOGISTICS_LIFT_GATE_REQUIRED")) {
        newValue = [...newValue, "LOGISTICS_LIFT_GATE_REQUIRED"];
      }
    }

    setSelectedAccessorials(newValue);
    props.selectedAccessorialsChanged(newValue);
  }

  return (
    <>
      {props.accessorials.map((accessorial) => (
        <AccessorialCheckbox_
          key={accessorial.type}
          accessorial={accessorial}
          checked={isAccessorialSelected(accessorial.type)}
          onChange={handleChange}
          challengeRemoval={describeChallengeRemoval(accessorial.type)}
          locationContext={props.locationContext}
        />
      ))}
    </>
  );
};

export default LocationsAccessorialsInput;

interface AccessorialCheckboxProps {
  accessorial: AccessorialType;
  checked: boolean;
  onChange: (type: string, checked: boolean) => void;
  challengeRemoval: ReactNode | undefined;
  locationContext: LocationContext;
}

function AccessorialCheckbox_(props: AccessorialCheckboxProps): JSX.Element {
  const { accessorial, checked, challengeRemoval } = props;

  // Only removal is challenged, so the confirm is passed only while checked.
  const confirm =
    checked && challengeRemoval !== undefined
      ? {
          title: CONFIRM_REMOVAL_TITLE,
          message: challengeRemoval,
          confirmLabel: "Remove",
        }
      : undefined;

  const checkbox = (
    <Checkbox
      label={
        <>
          {accessorial.name}
          {accessorial.questionBubble(props.locationContext)}
        </>
      }
      description={accessorial.description}
      checked={checked}
      // Unavailable services are greyed out rather than hidden: a customer
      // looking for inside delivery needs to find it and read why it's off,
      // and a missing checkbox reads as us having dropped the feature.
      disabled={
        (accessorial.comingSoon ?? false) || (accessorial.unavailable ?? false)
      }
      onChange={(next) => props.onChange(accessorial.type, next)}
      confirm={confirm}
      data-testid={`checkbox-${accessorial.type}`}
    />
  );

  if (accessorial.comingSoon) {
    return (
      <Tooltip side="right" content="Coming soon" block>
        {checkbox}
      </Tooltip>
    );
  }

  return checkbox;
}
